import { createMuiTheme } from '@material-ui/core/styles'
import { fontTypes as MaisonNeueExtended } from '~/assets/fonts/maison-neue-extended'
import { fontTypes as Roboto } from '~/assets/fonts/roboto'

const titleFonts = [
  '"Maison Neue Extended"',
  'Helvetica',
  'Arial',
  'Sans-serif'
].join(', ')

const baseFont = [
  'Roboto',
  'Helvetica',
  'Arial',
  'sans-serif'
].join(', ')

const palette = {
  inverse: true,
  background: {
    paper: '#F5F5F5',
    default: '#ffffff'
  },
  primary: {
    main: '#47413D'
  },
  secondary: {
    main: '#DBD4CD'
  },
  tertiary: {
    main: '#948880'
  },
  error: {
    main: '#FF6666'
  },
  warning: {
    main: '#FBC679'
  },
  info: {
    main: '#4577FA'
  },
  success: {
    main: '#70DD67'
  },
  'status-alert': {
    main: '#FBC679'
  },
  'brand-primary': {
    main: '#47413D'
  },
  'brand-secondary': {
    main: '#DBD4CD'
  },
  'brand-tertiary': {
    main: '#948880'
  },
  'neutral-ultra-dark': {
    main: '#000000'
  },
  'neutral-dark': {
    main: '#515151'
  },
  'neutral-medium': {
    main: '#878787'
  },
  'neutral-light': {
    main: '#F5F5F5'
  },
  'neutral-ultra-light': {
    main: '#FFFFFF'
  },
  'action-conversion-static': {
    main: '#4577FA'
  },
  'action-conversion-hover': {
    main: '#9174E4'
  },
  'action-conversion-disabled': {
    main: '#D6D6D6'
  },
  'action-navigation-static': {
    main: '#9174E4'
  },
  'action-navigation-hover': {
    main: '#4577FA'
  },
  'action-navigation-disabled': {
    main: '#D6D6D6'
  },
  'action-contrast-static': {
    main: '#FFFFFF',
    contrastText: '#515151'
  },
  'action-contrast-hover': {
    main: '#000000'
  },
  'action-contrast-disabled': {
    main: '#ffffff3d',
    contrastText: '#D6D6D6'
  },
  'support-whatsapp': {
    main: '#128C7E'
  },
  'support-facebook': {
    main: '#3B5998'
  },
  'support-BOT': {
    main: '#6F967E',
    text: '#ffffff',
    name: 'Boticário'
  },
  'support-EUD': {
    main: '#472D75',
    text: '#ffffff',
    name: 'Eudora'
  },
  'support-QDB': {
    main: '#DB5958',
    text: '#ffffff',
    name: 'Quem Disse Berenice'
  },
  'support-BLZ': {
    main: '#6A1B60',
    text: '#ffffff',
    name: 'Beleza na Web'
  },
  contrastThreshold: 3,
  tonalOffset: 0.2,
  date: {
    color: '#9174E4',
    name: 'roxa'
  }
}

export default createMuiTheme({
  palette,
  spacing: (i) => (i < 5 ? i * 4 : ((i - 2) * 8)),
  breakpoints: {
    values: {
      xs: 0,
      sm: 425,
      md: 1024,
      lg: 1440,
      xl: 1920,
      mobile: 425,
      tablet: 1024,
      desktop: 1440
    }
  },
  typography: {
    fontFamily: baseFont,
    fontWeight: 400,
    fontStyle: 'normal',
    h1: {
      fontFamily: titleFonts,
      fontSize: 96,
      lineHeight: '104px',
      fontWeight: 600
    },
    h2: {
      fontFamily: titleFonts,
      fontSize: 72,
      lineHeight: '80px',
      fontWeight: 600
    },
    h3: {
      fontFamily: titleFonts,
      fontSize: 56,
      lineHeight: '64px',
      fontWeight: 600
    },
    h4: {
      fontFamily: titleFonts,
      fontSize: 40,
      lineHeight: '48px',
      fontWeight: 600
    },
    h5: {
      fontSize: 32,
      lineHeight: '40px'
    },
    h6: {
      fontSize: 24,
      lineHeight: '32px'
    },
    body1: {
      fontSize: 16,
      lineHeight: '24px'
    },
    body2: {
      fontSize: 14,
      lineHeight: '24px'
    },
    subtitle1: {
      fontSize: 16,
      lineHeight: '20px'
    },
    subtitle2: {
      fontSize: 14,
      lineHeight: '16px'
    },
    caption: {
      fontSize: 12,
      lineHeight: '16px'
    },
    overline: {
      fontSize: 10,
      lineHeight: '16px',
      textTransform: 'uppercase',
      letterSpacing: '0.08em'
    }
  },
  overrides: {
    MuiDrawer: {
      paper: {
        width: 360,
        backgroundColor: '#F5F5F5'
      },
      paperAnchorDockedLeft: {
        position: 'relative'
      }
    },
    MuiButton: {
      root: {
        borderRadius: 4,
        height: 48
      },
      label: {
        color: 'inherit',
        fontSize: 14,
        fontWeight: 400
      },
      contained: {
        width: '100%',
        boxShadow: 'none',
        color: '#ffffff',
        '&.Mui-disabled': {
          '& .MuiButton-label': {
            opacity: 0.3
          }
        },
        '& .MuiCircularProgress-colorPrimary': {
          color: '#ffffff'
        }
      },
      sizeSmall: {
        height: 32
      },
      sizeLarge: {
        height: 64
      },
      containedPrimary: {
        '& .MuiButton-label': {
          color: '#000000'
        }
      }
    },
    MuiDivider: {
      light: {
        backgroundColor: '#ffffff'
      }
    },
    MuiTableCell: {
      head: {
        fontStyle: 'normal',
        fontSize: 10,
        lineHeight: '16px',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontWeight: 600
      },
      body: {
        fontStyle: 'normal',
        fontSize: 14,
        lineHeight: '24px',
        fontWeight: 400
      }
    },
    MuiListItem: {
      root: {
        paddingTop: 10,
        paddingBottom: 10,
        '&$selected': {
          backgroundColor: '#515151',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#5f5d5d'
          },
          '& .MuiListItemIcon-root': {
            color: '#ffffff'
          }
        }
      }
    },
    MuiRadio: {
      root: {
        '& .MuiSvgIcon-root': {
          fontSize: '1rem'
        }
      }
    },
    MuiOutlinedInput: {
      input: {
        padding: '0 12px',
        minHeight: 48
      }
    },
    MuiFormControl: {
      root: {
        '& .MuiTypography-subtitle2': {
          marginBottom: '4px'
        }
      }
    },
    MuiFormHelperText: {
      root: {
        textTransform: 'normal'
      }
    },
    MuiInputBase: {
      root: {
        backgroundColor: '#ffffff',
        '&.Mui-error .MuiInputBase-input': {
          color: '#DE1616'
        }
      },
      input: {
        height: 48,
        padding: '0 15px'
      }
    },
    MuiSelect: {
      selectMenu: {
        padding: '0 14px',
        minHeight: 48
      },
      select: {
        display: 'flex',
        alignItems: 'center'
      }
    },
    MuiFormLabel: {
      root: {
        lineHeight: '7px'
      }
    },
    MuiListItemIcon: {
      root: {
        justifyContent: 'center',
        minWidth: 40
      }
    },
    MuiListItemAvatar: {
      root: {
        minWidth: 32
      }
    },
    MuiAvatar: {
      square: {
        width: 24,
        height: 24
      }
    },
    MuiAlert: {
      filledSuccess: {
        backgroundColor: '#499D71'
      }
    },
    MuiTabs: {
      root: {
        minHeight: 32,
        '& .MuiTab-root:not(.Mui-selected) .MuiTab-wrapper': {
          borderBottom: 'solid 1px #878787'
        },
        '& .MuiTabScrollButton-root': {
          width: 24
        },
        '& .MuiTab-root': {
          minWidth: 'initial',
          minHeight: 32,
          padding: '0 16px 4px'
        },
        '& .MuiTabs-indicator': {
          height: 4,
          bottom: 3
        }
      }
    },
    MuiFormControlLabel: {
      root: {
        marginRight: 0
      },
      label: {
        '&.MuiTypography-body1': {
          flex: 1
        }
      }
    },
    MuiPickersBasePicker: {
      container: {
        alignItems: 'center'
      }
    },
    MuiPickersCalendar: {
      week: {
        '& .MuiPickersDay-current': {
          color: '#499D71',
          backgroundColor: 'white',
          border: '1px solid'
        },
        '& .MuiPickersDay-day': {
          color: '#499D71',
          backgroundColor: 'white',
          border: '1px solid'
        },
        '& .MuiPickersDay-dayDisabled': {
          color: 'rgb(170, 170, 170)',
          fontWeight: 'inherit',
          backgroundColor: 'inherit',
          border: 'none'
        },
        '& .MuiPickersAvailableDate .MuiButtonBase-root': {
          color: '#ffffff',
          fontWeight: 700,
          backgroundColor: '#65987C',
          border: '1px solid #65987C'
        }
      }
    },
    MuiPickersCalendarHeader: {
      iconButton: {
        '&:not(.Mui-disabled) .MuiIconButton-label': {
          background: '#499D71',
          color: 'white',
          borderRadius: '50%'
        }
      }
    },
    MuiPopover: {
      root: {
        zIndex: '999999 !important',
        '& .MuiMenuItem-root': {
          minHeight: '48px !important'
        },
        '& .MuiListItem-root.Mui-selected': {
          backgroundColor: 'transparent',
          color: palette['action-navigation-static'].main,
          paddingLeft: 24
        }
      }
    },
    MuiTooltip: {
      tooltip: {
        color: '#ffffff',
        background: 'linear-gradient(77.73deg, #8DC63F 0%, #3D9459 100%)',
        fontStyle: 'normal',
        fontSize: 12,
        lineHeight: '16px',
        fontWeight: 400,
        padding: 16
      },
      arrow: {
        color: '#6BB14A'
      }
    },
    MuiCssBaseline: {
      '@global': {
        a: {
          color: palette['action-conversion-static'].main,
          '&:hover': {
            color: palette['action-conversion-hover'].main
          }
        },
        '@font-face': [...MaisonNeueExtended, ...Roboto],
        '*::-webkit-scrollbar': {
          width: '0.4em'
        },
        '*::-webkit-scrollbar-track': {
          '-webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.00)'
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,.4)',
          outline: '1px solid slategrey',
          borderRadius: 3
        },
        '#root': {
          width: '100%',
          overflowX: 'hidden'
        },
        '.auxiliar': {
          fontSize: 12,
          lineHeight: '16px'
        },
        '.colorNeutralUltraDark': {
          color: palette['neutral-ultra-dark'].main
        },
        '.colorNeutralDark': {
          color: palette['neutral-dark'].main
        },
        '.colorNeutralMedium': {
          color: palette['neutral-medium'].main
        },
        '.colorNeutralLight': {
          color: palette['neutral-light'].main
        },
        '.boxCenter': {
          alignItems: 'center',
          display: 'flex',
          flex: 1
        }
      }
    }
  }
})
